'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Text, Container, Box, Loader, Stack, Card, Checkbox } from '@mantine/core';
import { useTranslations } from 'next-intl';
import RecommendedTestCard from '../RecommendedTestCard';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useUserId } from '@/contexts/UserContext';
import { getPackages } from '@/services/apiServices';

interface RecommendedTest {
  'CPT Code': string;
  Container: string;
  'Price (AED)': string;
  'Sample Type': string;
  TAT: string;
  'Test ID': number;
  'Test Name': string;
  reason: string;
}

interface PackagesResponse {
  packages: any[]; // We're not using this, but keeping it for type safety
  recommendedTests: RecommendedTest[];
}

interface PackagesStepProps {
  healthGoals: string[];
  diseaseTypes: string[];
  onPackageSelect?: (selectedTests: RecommendedTest[]) => void;
}

const PackagesStep: React.FC<PackagesStepProps> = ({
  healthGoals,
  diseaseTypes,
  onPackageSelect,
}) => {
  const t = useTranslations('recommendedTests');
  const [recommendedTests, setRecommendedTests] = useState<RecommendedTest[]>([]);
  const [selectedTests, setSelectedTests] = useState<RecommendedTest[]>([]);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMobile = useMediaQuery('(max-width: 767px)');
  const userId = useUserId();

  const calculateTotalAmount = useCallback((tests: RecommendedTest[]) => {
    const total = tests.reduce((sum, test) => {
      const priceString = test['Price (AED)'];
      const price = parseFloat(priceString.replace('AED ', '').replace(',', ''));
      return sum + (isNaN(price) ? 0 : price);
    }, 0);
    setTotalAmount(total);
  }, []);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        setIsLoading(true);
        const response = await getPackages(userId, healthGoals, diseaseTypes);

        if (!response || !response.data) {
          setError(t('apiResponseUndefined'));
          return;
        }

        const { recommendedTests: fetchedTests } = response.data as PackagesResponse;
        setRecommendedTests(fetchedTests);
        setSelectedTests(fetchedTests); // Initially select all tests
        calculateTotalAmount(fetchedTests);

        setError(null);
      } catch (err) {
        console.error('Error fetching packages:', err);
        setError(t('errorFetchingPackages'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchPackages();
  }, [userId, healthGoals, diseaseTypes, t, calculateTotalAmount]);

  // New useEffect to handle initial selection
  useEffect(() => {
    if (onPackageSelect && selectedTests.length > 0) {
      onPackageSelect(selectedTests);
    }
  }, [selectedTests, onPackageSelect]);

  const handleTestSelection = useCallback(
    (test: RecommendedTest, isSelected: boolean) => {
      setSelectedTests((prevSelectedTests) => {
        const updatedSelectedTests = isSelected
          ? [...prevSelectedTests, test]
          : prevSelectedTests.filter((t) => t['Test ID'] !== test['Test ID']);

        calculateTotalAmount(updatedSelectedTests);
        return updatedSelectedTests;
      });
    },
    [calculateTotalAmount]
  );

  if (isLoading) {
    return (
      <Container
        p={isMobile ? 'xs' : 'md'}
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}
      >
        <Loader />
      </Container>
    );
  }

  if (error) {
    return (
      <Container p={isMobile ? 'xs' : 'md'}>
        <Text color="red">{error}</Text>
      </Container>
    );
  }

  if (recommendedTests.length === 0) {
    return (
      <Container p={isMobile ? 'xs' : 'md'}>
        <Text>{t('noRecommendedTestsAvailable')}</Text>
      </Container>
    );
  }

  return (
    <Container p={isMobile ? 'xs' : 'md'}>
      <Stack>
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Text size="xl" fw={700} mb="md">
            {t('totalAmount')}: AED {totalAmount.toFixed(2)}
          </Text>
        </Card>
        <Box>
          <Text size={isMobile ? 'md' : 'lg'} fw={500} mb="md">
            {t('recommendedTests')}
          </Text>
          {recommendedTests.map((test, index) => (
            <Box key={index} mb="md">
              <Checkbox
                label={<RecommendedTestCard test={test} />}
                checked={selectedTests.some((t) => t['Test ID'] === test['Test ID'])}
                onChange={(event) => handleTestSelection(test, event.currentTarget.checked)}
              />
            </Box>
          ))}
        </Box>
      </Stack>
    </Container>
  );
};

export default PackagesStep;
